import type { VehicleListItem } from 'Types/vehicle';
import { DAY } from 'Utilities/consts';
import { getValueFromLocalStorage, setValueInLocalStorage } from 'Utilities/helpers/localStorage.helpers';

export const VIEWING_FILTER = {
	ALL: 'all',
	NEW: 'new',
	SEEN: 'seen',
} as const;

export type ViewingFilterValue = (typeof VIEWING_FILTER)[keyof typeof VIEWING_FILTER];

type SeenVehiclesCache = Record<string, number>;

const TWO_WEEKS_MS = 14 * DAY;

const getLocalStorageKey = (dealerId: number): string => `seen_vehicles_${dealerId}`;

const purgeStaleEntries = (data: SeenVehiclesCache): SeenVehiclesCache => {
	const cutoff = Date.now() - TWO_WEEKS_MS;
	const purged: SeenVehiclesCache = {};
	for (const [key, timestamp] of Object.entries(data)) {
		if (timestamp >= cutoff) {
			purged[key] = timestamp;
		}
	}
	return purged;
};

// In-memory cache — loaded once per dealer, updated on write
let activeDealerId: number | null = null;
let cache: SeenVehiclesCache | null = null;

const getSeenVehicles = (dealerId: number): SeenVehiclesCache => {
	if (cache !== null && dealerId === activeDealerId) return cache;
	activeDealerId = dealerId;
	try {
		const raw = getValueFromLocalStorage(getLocalStorageKey(dealerId));
		cache = raw ? (JSON.parse(raw) as SeenVehiclesCache) : {};
	} catch {
		cache = {};
	}
	return cache;
};

const writeToLocalStorage = (dealerId: number, data: SeenVehiclesCache): void => {
	try {
		setValueInLocalStorage({ id: getLocalStorageKey(dealerId), value: JSON.stringify(data) });
	} catch {
		// localStorage full or unavailable
	}
};

const updateCache = (dealerId: number, data: SeenVehiclesCache): void => {
	cache = data;
	writeToLocalStorage(dealerId, data);
};

const getLastSaleCycleStart = (todaysSaleStartedAt: Nullable<string>): number => {
	if (todaysSaleStartedAt) {
		return new Date(todaysSaleStartedAt).getTime();
	}

	const SALE_CYCLE_HOUR = 16;
	const SALE_CYCLE_MINUTE = 30;
	const now = new Date();
	const todayCycle = new Date(now.getFullYear(), now.getMonth(), now.getDate(), SALE_CYCLE_HOUR, SALE_CYCLE_MINUTE);
	return now >= todayCycle
		? todayCycle.getTime()
		: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, SALE_CYCLE_HOUR, SALE_CYCLE_MINUTE).getTime();
};

export const markVehicleAsSeen = (dealerId: number, vehicleId: number): void => {
	const current = purgeStaleEntries(getSeenVehicles(dealerId));
	const key = String(vehicleId);
	if (!current[key]) {
		current[key] = Date.now();
	}
	updateCache(dealerId, current);
};

export const filterVehiclesBySeen = (
	vehicles: VehicleListItem[],
	dealerId: number | undefined,
	filter: ViewingFilterValue,
	todaysSaleStartedAt?: Nullable<string>,
): VehicleListItem[] => {
	if (filter === VIEWING_FILTER.ALL || !dealerId) return vehicles;

	const current = getSeenVehicles(dealerId);
	const cycleStart = getLastSaleCycleStart(todaysSaleStartedAt ?? null);

	return vehicles.filter((vehicle) => {
		const timestamp = current[String(vehicle.id)];
		const seen = !!timestamp && timestamp < cycleStart;
		return filter === VIEWING_FILTER.SEEN ? seen : !seen;
	});
};
